import { useMemo } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Descendant } from 'slate';

import { FormulaType } from '../../constants/formulaType';
import { ToastStatus } from '../../constants/toastMessage.constants';

import { BaseError } from '../../helpers/BaseError';

import useQueryEmailTemplate from 'hooks/EmailTemplate/useQueryEmailTemplate';
import useCustomEmailTemplateDetail from 'hooks/custom-email-templates/useCustomEmailTemplateDetail';

import { FormulaTypesData } from '../../models/formula.model';
import { MessageContent } from '../../models/message-content.model';
import { ElementKind } from 'models/editor.model';

import API from '../../services/api';
import { toastService } from '../../services/toast.service';

import { getStructureFromSchema } from '../../views/surface/surfaceMessages/helper';

import useGetCustomSurfaceTemplate from '../custom-surface-template/useGetCustomSurfaceTemplate';
import formulaKeys from '../formulas/_queryKeys';
import useFormulaQuery from '../formulas/useFormulaQuery';
import useLanguagesQuery from '../languages/useLanguagesQuery';
import useSurfaceQuery from '../surface/useSurfaceQuery';

const addMessageContentToFormula = async (
    formulaId: number,
    languageId: string,
    structure?: Descendant[],
) => {
    const body: {
        language_id: string;
        formula_id: number;
        structure?: Descendant[];
    } = {
        language_id: languageId,
        formula_id: formulaId,
    };

    if (structure) {
        body.structure = structure;
    }
    const { data } = await API.addMessageContent(body);
    return data;
};

const useGetDefaultStructure = (
    formulaId: number,
): Descendant[] | undefined => {
    const { data: formula } = useFormulaQuery(formulaId, {});
    const surfaceId = formula?.surface_id;
    const formulaType = formula?.message_type as FormulaType;
    const { data: surface } = useSurfaceQuery(surfaceId!);
    const { data: customSurfaceTemplateData } = useGetCustomSurfaceTemplate(
        surface?.surface_template_id!,
        !!surface?.surface_template_id,
    );

    const isAampeCustomEmailTemplate = useMemo<boolean | undefined>(() => {
        return formula?.additional_metadata?.is_aampe_custom_template;
    }, [formula]);

    const emailTemplateId = useMemo<string | undefined>(() => {
        return formula?.additional_metadata?.email_template_id;
    }, [formula]);

    const { data: templateData } = useQueryEmailTemplate(
        emailTemplateId as string,
        formulaType,
        !isAampeCustomEmailTemplate,
    );
    const { data: customTemplateData } = useCustomEmailTemplateDetail(
        emailTemplateId as string,
        isAampeCustomEmailTemplate,
    );
    const templateDynamicFields = useMemo<string[]>(() => {
        if (isAampeCustomEmailTemplate)
            return [
                ...new Set(
                    customTemplateData?.additional_metadata?.dynamic_keys ?? [],
                ),
            ];
        return [...new Set(templateData?.dynamicFieldsWithModifiers ?? [])];
    }, [customTemplateData, isAampeCustomEmailTemplate, templateData]);

    const emailTemplate = useMemo<Descendant[]>(() => {
        const fixedFields = FormulaTypesData[formulaType]?.getTemplate() || [];
        const dynamicFields =
            (templateDynamicFields.map((field) => ({
                kind: ElementKind.section,
                sectionType: field,
                children: [{ text: '' }],
            })) as Descendant[]) || [];
        return [...fixedFields, ...dynamicFields];
    }, [formulaType, templateDynamicFields]);

    if (surface) {
        let surfaceSchema = surface?.surface_schema;

        // if the surface has a custom template, use the custom template
        if (
            surface?.surface_template_id &&
            customSurfaceTemplateData?.template
        ) {
            surfaceSchema = customSurfaceTemplateData?.template;
        }

        return getStructureFromSchema(surfaceSchema ?? {});
    }

    if (formula?.message_type) {
        if (formulaType == FormulaType.email) {
            return emailTemplate;
        }
        return FormulaTypesData[formula.message_type].getTemplate();
    }
};

export default function useAddMessageContentToFormula(formulaId: number) {
    const { data: languages } = useLanguagesQuery();

    const defaultStructure = useGetDefaultStructure(formulaId);

    const queryClient = useQueryClient();
    return useMutation<MessageContent, BaseError, string>({
        mutationFn: (languageId: string) =>
            addMessageContentToFormula(formulaId, languageId, defaultStructure),
        onSuccess: (data) => {
            const languageName = languages?.find(
                (language) => language.id === data.language_id,
            )?.name;

            toastService.showNotificationToast(
                ToastStatus.Success,
                `${languageName} added to message`,
            );

            queryClient.invalidateQueries({
                queryKey: formulaKeys.formulaMessageContents(formulaId),
            });
        },
    });
}
